import { colors2 } from "oolib";

/**
 * Maps a value from an input range to a color
 * with the base color at the highest value of the range
 * @param {number} value - The value to map
 * @param {number} minValue - The minimum value in the input range
 * @param {number} maxValue - The maximum value in the input range
 * @param {Object} hslColor - The base HSL color object for highest values
 * @param {number} hslColor.h - Hue component (0-360)
 * @param {number} hslColor.s - Saturation component (0-100)
 * @param {number} hslColor.l - Lightness component (0-100)
 * @param {number} minLightness - The minimum lightness (0-100) for lowest values
 * @param {number} maxLightness - The maximum lightness (0-100) for highest values
 * @param {boolean} plusMinusMode - Diverge from zero rather than flooring at it
 * @return {string} - A hex color code
 */
export const mapValueToColor = (
    value,
    minValue,
    maxValue, 
    plusMinusMode = false,
    // hslColor = {h: 252, s:100, l:71 }, // Default to #7B5AFF 
    // minLightness = 20,
    // maxLightness = 98
  ) => {
    // PLUS-MINUS: the sign is the reading, so the scale diverges from zero
    // instead of flooring there — the standard ramp climbing on the positive
    // side, its inverse (the barriers terracotta) deepening on the negative.
    // Both are scaled against the SAME magnitude, so -40 and +40 read as
    // equally strong in opposite directions. `maxValue` is that magnitude.
    if (plusMinusMode) {
      if (!value) return `hsl(0, 0%, 95%)`;
      const magnitude = Math.max(Math.abs(maxValue), Math.abs(minValue), 1);
      const strength = Math.min(1, Math.abs(value) / magnitude);
      // Positive: the grid's own purple. Negative: terracotta (#D4553A).
      const { h, s, l } = value > 0 ? { h: 252, s: 83, l: 56 } : { h: 12, s: 64, l: 53 };
      // Full accent at the strongest, near-white at the faintest.
      return `hsl(${h}, ${s}%, ${l + (94 - l) * (1 - strength)}%)`;
    }

    // Ensure value is within range
    value = Math.max(minValue, Math.min(maxValue, value));

    // Handle zero or negative values - return HSL format instead of colors2.grey5
    if(value <= 0) {
      return `hsl(0, 0%, 95%)`; // Light grey in HSL format
    }
    
    // Handle division by zero: if maxValue === minValue
    if (maxValue === minValue || maxValue <= minValue) {
      return `hsl(252, 100%, 93%)`; // Return lowest color
    }
    
    // Define the color range
    // Highest color: #5630EC - hsl(252, 83%, 56%)
    // Lowest color: #E2DBFF - hsl(252, 100%, 93%)
    const highestColor = { h: 252, s: 83, l: 56 };
    const lowestColor = { h: 252, s: 100, l: 93 };
    
    // Normalize value to 0-1 range
    const normalizedValue = (value - minValue) / (maxValue - minValue);
    
    // Interpolate between lowest and highest colors
    const h = lowestColor.h + normalizedValue * (highestColor.h - lowestColor.h);
    const s = lowestColor.s + normalizedValue * (highestColor.s - lowestColor.s);
    const l = lowestColor.l + normalizedValue * (highestColor.l - lowestColor.l);
    
    return `hsl(${h}, ${s}%, ${l}%)`
  }
  